import { useEffect, useState } from "react";
import { Loader2, Search, Sticker, X } from "@fg/icons";

/**
 * Teams-style GIF picker. Searches Tenor v2 for the query the user
 * types and renders a grid of `tinygif` thumbnails. Clicking a tile
 * resolves the selection back to the parent so the composer can post
 * the GIF as a message body.
 *
 * No-key fallback: when VITE_TENOR_API_KEY is unset (dev / CI),
 * renders an inline hint instead of broken empty searches. Tenor's
 * key is browser-safe — it's a public read-only API key — so we ship
 * it with the build via the Vite env var.
 */
const TENOR_KEY = import.meta.env.VITE_TENOR_API_KEY ?? "";
const TENOR_BASE = "https://tenor.googleapis.com/v2";

interface TenorResult {
    id: string;
    title: string;
    media_formats: {
        tinygif?: { url: string };
        gif?: { url: string };
    };
}

export function GifPicker({
    onSelect,
    onClose
}: {
    onSelect: (url: string, alt: string) => void;
    onClose: () => void;
}) {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState<TenorResult[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!TENOR_KEY) return;
        if (query.trim().length < 2) {
            void runFeatured();
            return;
        }
        const handle = window.setTimeout(() => void runSearch(query.trim()), 300);
        return () => window.clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query]);

    async function runFeatured() {
        if (!TENOR_KEY) return;
        setLoading(true); setError(null);
        try {
            const url = `${TENOR_BASE}/featured?key=${TENOR_KEY}&client_key=fg-connect&limit=24&media_filter=tinygif,gif`;
            const res = await fetch(url);
            if (!res.ok) throw new Error(`Tenor ${res.status}`);
            const body = (await res.json()) as { results: TenorResult[] };
            setResults(body.results ?? []);
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setLoading(false);
        }
    }

    async function runSearch(q: string) {
        setLoading(true); setError(null);
        try {
            const url = `${TENOR_BASE}/search?q=${encodeURIComponent(q)}&key=${TENOR_KEY}&client_key=fg-connect&limit=24&media_filter=tinygif,gif`;
            const res = await fetch(url);
            if (!res.ok) throw new Error(`Tenor ${res.status}`);
            const body = (await res.json()) as { results: TenorResult[] };
            setResults(body.results ?? []);
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setLoading(false);
        }
    }

    return (
        <div
            role="dialog"
            aria-label="Pick a GIF"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="flex w-full max-w-lg flex-col rounded-lg border bg-card shadow-2xl">
                <header className="flex items-center justify-between border-b px-4 py-3">
                    <div className="flex items-center gap-2">
                        <Sticker className="h-4 w-4 text-primary" aria-hidden />
                        <h2 className="text-base font-semibold">Pick a GIF</h2>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 hover:bg-accent">
                        <X className="h-4 w-4" />
                    </button>
                </header>
                <div className="border-b px-4 py-2">
                    <div className="relative">
                        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search Tenor"
                            disabled={!TENOR_KEY}
                            autoFocus
                            className="w-full rounded-md border bg-background pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                        />
                    </div>
                </div>
                <div className="min-h-[280px] flex-1 overflow-auto p-2">
                    {!TENOR_KEY ? (
                        <p className="p-6 text-center text-xs text-muted-foreground">
                            GIF search is unavailable — set <code>VITE_TENOR_API_KEY</code>{" "}
                            in <code>EnterpriseMeeting/web/.env.local</code> to enable.
                        </p>
                    ) : loading ? (
                        <p className="flex items-center justify-center gap-2 p-6 text-xs text-muted-foreground">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…
                        </p>
                    ) : error ? (
                        <p className="p-6 text-center text-xs text-destructive">
                            Couldn't load GIFs: {error}
                        </p>
                    ) : results.length === 0 ? (
                        <p className="p-6 text-center text-xs text-muted-foreground">No GIFs match.</p>
                    ) : (
                        <div className="grid grid-cols-3 gap-1.5">
                            {results.map((r) => {
                                const tiny = r.media_formats.tinygif?.url ?? r.media_formats.gif?.url;
                                const full = r.media_formats.gif?.url ?? r.media_formats.tinygif?.url;
                                if (!tiny || !full) return null;
                                return (
                                    <button
                                        key={r.id}
                                        type="button"
                                        onClick={() => onSelect(full, r.title || "GIF")}
                                        className="overflow-hidden rounded-md border hover:border-primary"
                                    >
                                        <img src={tiny} alt={r.title || "GIF"} className="h-24 w-full object-cover" loading="lazy" />
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
                <footer className="border-t px-4 py-2 text-[10px] text-muted-foreground">
                    GIFs via Tenor
                </footer>
            </div>
        </div>
    );
}
